import { z } from 'zod';

import { ConversationStatusSchema } from '../../chat/conversation-status/conversation-status';
import { UuidSchema } from '../../common/id/id';
import { GuardianCounterpartySchema } from '../guardian-counterparty/guardian-counterparty';

/**
 * One of a ward's conversations, as her guardian reads it.
 *
 * ## It is not `ConversationSchema`, and the difference is the point
 *
 * Chat's own summary carries `unreadCount` and `otherLastReadAt`, which are
 * **her** read state. A guardian has none — he never marks anything read, and
 * there is no route by which he could — so a badge here would not be his own
 * unread count but a report on how promptly she replies. That is surveillance of
 * the ward rather than supervision of the courtship, and the two are worth
 * keeping apart in the payload rather than in a client's restraint.
 *
 * `otherUserId` is absent for a narrower reason: the conversation id is this
 * row's key, so an id the composer **declined to resolve** never travels on its
 * own. When `counterparty` is `null` the honest statement is "there is a
 * conversation here and the other party cannot be shown", not a bare uuid.
 *
 * ## `counterparty` is nullable, and that is a third state with its own copy
 *
 * `null` means the composer could not draw him — a closed account, a blocked
 * pair, a member no longer approved. The row **stays**: dropping it would tell a
 * wali his ward has fewer conversations than she has, which is worse than
 * telling him one of them cannot be displayed. A client owes this case a
 * rendering (P23c2a — a lookup that omits what it cannot resolve turns a
 * two-way label into three states, and the third has no copy anywhere yet).
 */
export const GuardianWardConversationSchema = z
  .object({
    id: UuidSchema,
    matchId: UuidSchema,
    status: ConversationStatusSchema,
    lastMessageAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    counterparty: GuardianCounterpartySchema.nullable(),
  })
  .strict();

export type GuardianWardConversation = z.infer<typeof GuardianWardConversationSchema>;
